import {
  buildEditableLibraries,
  cellDisplayValue,
  CellPosition,
  EditableLaborLibrary,
  isOverridden,
  OverrideHalf,
  overridesSnapshot,
  scrubKeepingSelection,
  setOverride,
  toOverridesPayload
} from '../data/labor-rate-override-grid';
import { CurrencyCode } from '../../api/dealer-api-interface-all';
import {
  currencyCodeToString,
  moneyToStr,
  stringToCurrencyCode
} from '../../../webmodule-common/other/currency-formatter';
import {
  loadLaborRateMatrix,
  MatrixLoad,
  MatrixSelectionCodes,
  resolveSelection
} from '../data/labor-rate-matrix-load';
// eslint-disable-next-line import/named
import { customElement } from 'lit/decorators.js';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { getApiFactory } from '../../api/api-injector';
import { GlobalSupplierNotification } from '../../v6config/supplier-services';
import { html, nothing } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { LangFranchiseeLaborRateMatrixView } from '../../../localization/scripts/franchisee/view/franchisee-labor-rate-matrix-view-lang';
import { live } from 'lit/directives/live.js';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import {
  ResultGetLaborRateMatrix,
  v6Config,
  v6SupportsVersion,
  v6Util,
  v6VersionMap
} from '../../../webmodule-common/interop/interfaces/v6-config';
import { userSecurity } from '../../../webmodule-common/other/api/user-security';
import { v6BootLoader } from '../../v6config/v6config';
import { WebmoduleChangeEvent, WebmoduleSelect } from '../../../webmodule-common/components/src/webmodule-components';

// What every cell of one render shares, worked out once per grid rather than once per cell.
interface CellRenderContext {
  library: EditableLaborLibrary;
  half: OverrideHalf;
  areas: string[];
  types: string[];
  readonly: boolean;
  formatRate: (value: string) => string;
}

@customElement('wm-franchiseelaborratematrixview')
export class FranchiseeLaborRateMatrixView extends PageControlTabWithIndependantSaving {
  // Null while loading. A loaded result carries the supplier the matrix was read from through to the
  // save. Overrides are stored per supplier, and a set only lines up with the libraries and axis codes
  // of the V6 it was edited against - resolving the supplier again at save time could attach it to a
  // different matrix.
  private load: MatrixLoad | null = null;
  private libraries: EditableLaborLibrary[] = [];
  private libraryIndex = 0;
  private layerIndex = 0;
  private half: OverrideHalf = 'rates';
  private savedSnapshot = overridesSnapshot([]);
  // Key of the cell currently focused (area and type), or null when none is.
  private focusedCell: string | null = null;

  constructor() {
    super();
    this.pageFragment = 'labor-rates';
    GlobalSupplierNotification.getDefaultInstance()?.addEventListenter(this.supplierStatusChanged);
  }

  async dispose(): Promise<void> {
    GlobalSupplierNotification.getDefaultInstance()?.remEventListenter(this.supplierStatusChanged);
    await super.dispose();
  }

  // Why listen at all? The matrix is otherwise only read on entering the tab, so a supplier coming back
  // while the tab is open would leave the offline message up until the user left and came back.
  // Why wait for the bootloader? The frame config version is only known once it has loaded, and a
  // supplier that was offline may not have had one yet, which reads as unsupported until it does.
  private readonly supplierStatusChanged = async (gsn: GlobalSupplierNotification): Promise<void> => {
    if (!gsn.online || (this.load?.state !== 'supplierOffline' && this.load?.state !== 'unsupported')) return;
    await v6BootLoader();
    await this.onEnter();
  };

  private get loaded(): ResultGetLaborRateMatrix | null {
    return this.load?.state === 'loaded' ? this.load.loaded : null;
  }

  public isDataReadonly(): boolean {
    return !(userSecurity().isAdmin() || userSecurity().isSupplier());
  }

  public allowDeletePage(): boolean {
    return false;
  }

  public internalDataChanged(): boolean {
    return overridesSnapshot(this.libraries) !== this.savedSnapshot;
  }

  async onEnter(): Promise<void> {
    // Why read again on every entry? The matrix is never cached dealer-side, so coming back to the tab
    // is how a fabricator sees a default that changed in V6. Unsaved edits are safe: canLeave guards them.
    const previousSelection = this.selectionCodes();
    this.load = null;
    this.requestUpdate();

    // Why check here rather than hide the tab? A supplier on an older frame config cannot read the matrix
    // at all, and saying so tells the fabricator why there is nothing to edit.
    this.load = v6SupportsVersion(v6VersionMap.hasLaborRateMatrix)
      ? await loadLaborRateMatrix(
          () => v6Config().suppliers(),
          supplierId => v6Util().getLaborRateMatrix(supplierId)
        )
      : { state: 'unsupported' };

    // Why not reset the selection? Leaving and returning to the tab should not throw away which library
    // and layer the user was on; resolveSelection finds them again in the freshly read matrix.
    const selection = resolveSelection(this.loaded?.matrix.libraries ?? [], previousSelection);
    this.libraryIndex = selection.libraryIndex;
    this.layerIndex = selection.layerIndex;
    this.resetEdits();
    this.requestUpdate();
  }

  protected async revertChanges(): Promise<boolean> {
    await super.revertChanges();
    this.resetEdits();
    this.requestUpdate();
    return true;
  }

  public async prepareForSave(): Promise<void> {
    // Why override with nothing in it? The base implementation throws unless overridden, but edits land
    // directly in the grids as they happen and are normalised as they are entered, so there is nothing
    // left to stage or validate here.
  }

  protected async internalSaveData(): Promise<boolean> {
    if (this.load?.state !== 'loaded') return false;
    const result = await getApiFactory()
      .franchisee()
      .updateFabricatorRateOverrides({
        supplierId: this.load.supplierId,
        overrides: toOverridesPayload(this.libraries)
      });
    if (!result) return false;

    this.load = { ...this.load, loaded: { ...this.load.loaded, overrides: result.overrides } };
    this.resetEdits();
    if (!isAutoSaving()) fireQuickSuccessToast(LangFranchiseeLaborRateMatrixView.laborRatesSaved());
    this.requestUpdate();
    return true;
  }

  private selectionCodes(): MatrixSelectionCodes | null {
    const library = this.libraries[this.libraryIndex]?.defaults;
    if (!library) return null;
    return { libraryCode: library.libraryCode, layerCode: library.layers[this.layerIndex] ?? '' };
  }

  private resetEdits(): void {
    const loaded = this.loaded;
    this.libraries = loaded ? buildEditableLibraries(loaded.matrix.libraries, loaded.overrides) : [];
    this.savedSnapshot = overridesSnapshot(this.libraries);
  }

  protected getCaption(): Snippet {
    return LangFranchiseeLaborRateMatrixView.laborRates();
  }

  protected bodyTemplate(): EventTemplate {
    if (!this.load) return this.message(LangFranchiseeLaborRateMatrixView.loading());
    if (this.load.state === 'supplierOffline') return this.message(LangFranchiseeLaborRateMatrixView.supplierOffline());
    if (this.load.state === 'unsupported') return this.message(LangFranchiseeLaborRateMatrixView.unsupported());
    if (this.load.state === 'failed') return this.message(LangFranchiseeLaborRateMatrixView.loadFailed());
    if (this.libraries.length === 0) return this.message(LangFranchiseeLaborRateMatrixView.noLibraries());

    const library = this.libraries[this.libraryIndex];
    const currency = this.rateCurrency();
    return html`
      <div class="labor-rate-matrix">
        <div class="labor-rate-matrix-selectors page-dashboard-filter">
          ${this.libraries.length > 1 ? this.librarySelect() : nothing}
          ${library.defaults.layers.length > 1 ? this.layerSelect(library) : nothing} ${this.settingSelect()}
          ${this.legendCaption(currency)}
        </div>
        ${this.gridTemplate(library, currency)}
      </div>
    `;
  }

  private message(text: string): EventTemplate {
    return html`<div class="alert alert-info m-3" role="alert">${text}</div>`;
  }

  // Why the select's own label rather than a <label for>? webmodule-select is not a labelable element, so a
  // for/id link does nothing; its label prop renders inside the shadow root, where it names the combobox and
  // focuses it on click.
  // Why live() on the value? A change is held back until switchSelection's save succeeds; when it fails
  // the bound value has not changed, so without live() Lit would skip it and the select would keep
  // showing the rejected choice over the old grid.
  private selectGroup(
    label: string,
    value: string,
    values: { value: string; text: string }[],
    onChange: (value: string) => Promise<void>
  ): EventTemplate {
    return html`<div class="labor-rate-select-group">
      <webmodule-select
        size="small"
        label=${label}
        .value=${live(value)}
        .values=${values}
        @webmodule-change=${async (e: WebmoduleChangeEvent) =>
          await onChange(String((e.target as WebmoduleSelect).value))}
      >
      </webmodule-select>
    </div>`;
  }

  private librarySelect(): EventTemplate {
    return this.selectGroup(
      LangFranchiseeLaborRateMatrixView.library(),
      String(this.libraryIndex),
      this.libraries.map((library, index) => ({
        value: String(index),
        text: library.defaults.libraryDescription || library.defaults.libraryCode
      })),
      async value =>
        await this.switchSelection(() => {
          this.libraryIndex = Number(value);
          // Why reset the layer? Each library has its own layers, so a position from the previous one means nothing here.
          this.layerIndex = 0;
        })
    );
  }

  // Why a different legend per half? Factors are dimensionless multipliers, so labelling them with a
  // currency would misdescribe them - the rates half needs the currency named, the factors half doesn't.
  private legendCaption(currency: CurrencyCode): EventTemplate {
    const legend =
      this.half === 'rates'
        ? LangFranchiseeLaborRateMatrixView.ratesInCurrency(currencyCodeToString(currency))
        : LangFranchiseeLaborRateMatrixView.factorsLegend();
    return html`<div class="labor-rate-currency-caption align-self-center">${legend}</div>`;
  }

  private layerSelect(library: EditableLaborLibrary): EventTemplate {
    return this.selectGroup(
      LangFranchiseeLaborRateMatrixView.layer(),
      String(this.layerIndex),
      library.defaults.layers.map((layerCode, index) => ({ value: String(index), text: layerCode })),
      async value => await this.switchSelection(() => (this.layerIndex = Number(value)))
    );
  }

  private settingSelect(): EventTemplate {
    return this.selectGroup(
      LangFranchiseeLaborRateMatrixView.setting(),
      this.half,
      [
        { value: 'factors', text: LangFranchiseeLaborRateMatrixView.settingOption('factors') },
        { value: 'rates', text: LangFranchiseeLaborRateMatrixView.settingOption('rates') }
      ],
      async value => await this.switchSelection(() => (this.half = value as OverrideHalf))
    );
  }

  private gridTemplate(library: EditableLaborLibrary, currency: CurrencyCode): EventTemplate {
    const { areas, types } = library.defaults;
    const layer = this.layerIndex;
    const context: CellRenderContext = {
      library,
      half: this.half,
      areas,
      types,
      readonly: this.isDataReadonly(),
      formatRate: value => moneyToStr(value, currency)
    };
    return html`<div class="labor-rate-grid-scroll">
      <table class="labor-rate-grid">
        <thead>
          <tr>
            <th></th>
            ${types.map(typeCode => html`<th scope="col">${typeCode}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${areas.map(
            (areaCode, area) =>
              html`<tr>
                <th scope="row">${areaCode}</th>
                ${types.map((_typeCode, type) => this.cellTemplate(context, { layer, area, type }))}
              </tr>`
          )}
        </tbody>
      </table>
    </div>`;
  }

  private cellTemplate(context: CellRenderContext, cell: CellPosition): EventTemplate {
    const { library, half } = context;
    // Why only area and type? Library and layer cannot change while a cell has focus: picking either
    // means clicking a select, which blurs the cell first.
    const key = `${cell.area}-${cell.type}`;
    const displayValue = cellDisplayValue(library, half, cell, this.focusedCell === key, context.formatRate);
    // Why no class for the focused cell? The stylesheet lights it with :focus-within, so the colour
    // follows the caret without the render having to catch up with it.
    return html`<td class=${isOverridden(library, half, cell) ? 'labor-rate-cell overridden' : 'labor-rate-cell'}>
      <div class="labor-rate-cell-body">
        <input
          class="form-control form-control-sm"
          inputmode="decimal"
          aria-label=${LangFranchiseeLaborRateMatrixView.cellLabel(
            half,
            context.areas[cell.area],
            context.types[cell.type]
          )}
          ?readonly=${context.readonly}
          .value=${live(displayValue)}
          @input=${this.overrideInputEvent}
          @change=${(e: Event) => this.overrideChangeEvent(library, half, cell, (e.target as HTMLInputElement).value)}
          @focus=${() => this.setFocusedCell(key)}
          @blur=${() => this.setFocusedCell(null)}
        />
      </div>
    </td>`;
  }

  // Why convert rather than cast? V6 sends the matrix currency as an ISO alpha string, but moneyToStr
  // takes the numeric CurrencyCode enum, so a bare cast would match nothing and silently show USD.
  // Why resolve it once in bodyTemplate? An unrecognised code falls back to USD, so the legend has to be
  // named from the resolved code too - naming it from the raw string would caption a grid of $ values
  // with a currency the cells are not in.
  private rateCurrency(): CurrencyCode {
    return stringToCurrencyCode(this.loaded?.matrix.currency);
  }

  // Why hold the new selection back until the save? Pending edits belong to the grid on screen, so they
  // are saved first; if that fails the old grid stays and the live() select bindings put the selects
  // back to match it.
  private async switchSelection(apply: () => void): Promise<void> {
    if (this.isDataReadonly() || (await this.performAutoSave())) apply();
    this.requestUpdate();
  }

  private overrideInputEvent(e: Event): void {
    const input = e.target as HTMLInputElement;
    const end = input.value.length;
    const scrubbed = scrubKeepingSelection(input.value, input.selectionStart ?? end, input.selectionEnd ?? end);
    // Why not requestUpdate() here? A re-render mid-keystroke would reformat the field from state; the
    // element only needs scrubbing in place, the same way the house input-money does. The cell commits
    // on change.
    if (scrubbed.value === input.value) return;
    input.value = scrubbed.value;
    input.setSelectionRange(scrubbed.selectionStart, scrubbed.selectionEnd);
  }

  private overrideChangeEvent(
    library: EditableLaborLibrary,
    half: OverrideHalf,
    cell: CellPosition,
    input: string
  ): void {
    setOverride(library, half, cell, input);
    this.requestUpdate();
  }

  private setFocusedCell(key: string | null): void {
    this.focusedCell = key;
    this.requestUpdate();
  }
}
